<style scoped>
.v-radio i {
  margin-top: 24px !important;
}
</style>

<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap class="mt-3">
      <v-flex xs12>
        <Stepper step="3"></Stepper>
      </v-flex>

      <ShipmentInfo :volumetricWeight="volumetricWeight" :shipment="shipment" @fetch="fetch"></ShipmentInfo>

      <v-flex xs12>
        <v-data-table class="elevation-1 shipments-rates"
                      :items="rates"
                      :loading="loading"
                      hide-headers
                      hide-actions
                      :no-data-text="noDataText">
          <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>

          <template v-slot:items="props">
            <tr class="hidden-row"></tr>
            <tr :active="selectedRate === props.item" @click="selectedRate = props.item; pickupCheckboxActive = props.item.service.code">
              <td width="5%">
                <v-radio-group v-model="selectedRate">
                  <v-radio :value="props.item" />
                </v-radio-group>
              </td>

              <td :class="props.item.service_has_pickup ? 'logo-checkbox' : ''">
                <img :src="props.item.image.url" :alt="props.item.carrier_name" width="100" />

                <v-layout :class="pickupCheckboxActive === props.item.service.code && props.item.service_has_pickup ? 'pickup-checkbox' : ''" v-if="props.item.service_has_pickup">
                  <v-checkbox :label="$t('scheduleCollection')" v-model="isPickup" v-if="selectedRate && selectedRate.service_name === props.item.service_name" hide-details class="shrink mr-2">
                    <v-tooltip slot="append" max-width="300px" min-width="200px" bottom>
                      <v-btn slot="activator" color="primary" flat icon>
                        <font-awesome-icon icon="info-circle"></font-awesome-icon>
                      </v-btn>
                      <span>
                        {{ $t('schedulePickup') }}
                      </span>
                    </v-tooltip>
                  </v-checkbox>
                </v-layout>

                <v-layout :class="'pickup-checkbox'" v-if="canInsurePackage(props.item)">
                  <v-checkbox :label="$t('attrs.insure_package')" v-model="props.item.insure_package" hide-details class="shrink mr-2"></v-checkbox>
                  <span class="mt-1 primary--text">+{{ calculateInsurance(props.item) | currency }}</span>
                </v-layout>
              </td>

              <td>
                {{ props.item.service_name }}
                <span class="error--text" v-if="props.item.extended_zone">
                  ({{ $t('attrs.extended_zone') }})
                </span>
              </td>

              <td> {{ props.item.days }} {{ $t('attrs.days') }} </td>

              <RateCosts :rate="props.item" :showDiscount="showDiscount" :isOperatorAdminRates="isOperatorAdminRates"></RateCosts>
            </tr>
          </template>
        </v-data-table>
      </v-flex>

      <v-flex xs12 class="text-xs-right">
        <v-btn color="primary" flat @click="goTo('shipments')" :loading="loading" :disabled="loading">{{ $t('cancel') }}</v-btn>
        <v-btn color="primary" @click="submit" :disabled="!selectedRate" :loading="loading">
          {{ $t('saveContinue') }}
        </v-btn>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import Shipment from '@/client/shipment'
import Rate from '@/client/rate'
import Setting from '@/client/setting'

import Stepper from '@/components/shipments/stepper'
import ShipmentInfo from '@/components/shipments/info'
import RateCosts from '@/components/RateCosts'

export default {
  name: 'ParcelFormView',

  props: {},

  data () {
    return {
      loading: false,
      shipment: {},
      rates: [],
      selectedRate: null,
      isPickup: false,
      pickupCheckboxActive: false,
      settings: [],
      volumetricWeight: '',
      isOperatorAdminRates: false,
      showAdditionalStaticRates: ''
    }
  },

  components: {
    Stepper,
    ShipmentInfo,
    RateCosts,
  },

  computed: {
    showDiscount () {
      if (!this.settings) { return }

      const setting = this.settings.find(o => o.key === 'show_discount') || {}
      return setting.value === '1'
    },

    noDataText () {
      if (this.loading && (!this.rates || !this.rates.length)) {
        return this.$t('loading')
      }

      if (!this.loading && (!this.rates || !this.rates.length)) {
        return this.$t('noDataAvailable')
      }

      return ""
    },
  },

  methods: {
    async fetch () {
      this.loading = true
      try {
        this.rates = []
        this.shipment = await Shipment.findOne(this.$route.params.shipmentId)

        if (this.shipment.step === 'confirmed') {
          this.goTo('Shipments')
        }

        Rate.shipmentId = this.shipment.id

        this.volumetricWeight = await Rate.volumetricWeight()
        this.rates = await Rate.all()
      } catch (err) {
        console.log(err)
        this.handleErr(err)
      }
      this.loading = false
    },

    async submit () {
      const isValid = await this.$validator.validateAll()
      if (!isValid) { return }
      if (!this.selectedRate) { return }

      this.loading = true
      try {
        this.selectedRate.is_pickup = this.isPickup
        this.selectedRate.is_from_app = true

        let response = await Rate.create(this.selectedRate)
        let shipment = response.shipment

        if (shipment.source == 'manual' && shipment.status == 'success') {
          this.setNotification({
            color: 'success',
            message: this.$t('manualShipmentSuccess', { id: shipment.id })
          })
          if (this.$store.state.currentUser.id == response.user.id) this.$store.state.userBalance = response.user.balance
          this.goTo('shipments')
        } else {
          this.goTo('shipmentConfirm', this.shipment.id)
        }
      } catch (err) {
        this.handleErr(err)
        this.rates = await Rate.all()
      }
      this.loading = false
    },

    canInsurePackage(rate) {
      const service = rate.service
      if (!service) { return false }

      const carrier = service.carrier
      if (!carrier) { return false }

      return carrier.insurance_type !== "none"
    },

    calculateInsurance(rate) {
      const service = rate.service
      if (!service) { return 0 }

      const carrier = service.carrier
      if (!carrier) { return 0 }

      const amount = parseFloat(carrier.insurance_amount)
      const percentage = parseFloat(carrier.insurance_percentage) / 100

      const declaredValue = parseFloat(this.shipment.parcels[0].declared_tax_value)
      return (declaredValue * percentage) + amount
    },

    setPackageLimitMessage (item) {
      if (item.parcel_size_max_limit) {
        return this.$t('parcelSizeExceededLast', { min_limit: item.parcel_size_min_limit, max_limit: item.parcel_size_max_limit })
      }
      else {
        return this.$t('parcelSizeExceededFirst', { limit: item.parcel_size_min_limit })
      }
    }
  },

  watch: {
    selectedRate () {
      this.isPickup = false;
    }
  },

  async mounted () {
    this.settings = await Setting.all()
    const operatorSetting = this.settings.find(o => o.key === 'operator_visible_rates') || {}
    const showAdditionalStaticRates = this.settings.find(o => o.key === 'show_additional_static_rates') || {}


    this.showAdditionalStaticRates = showAdditionalStaticRates.value == '1'
    this.isOperatorAdminRates = operatorSetting.value == '1'

    await this.fetch()
    const newShipment = JSON.parse(window.sessionStorage.getItem('new_shipment') || '{}')
    if (newShipment.selectedRate) {
      this.selectedRate = this.rates.find(o => o.service_id === newShipment.selectedRate.service_id)
    }
  }
}
</script>
